import type { DocumentBlock } from "@magic-md/core";

export function areDocumentBlocksRenderEqual(
    left: DocumentBlock,
    right: DocumentBlock,
): boolean {
    if (left.type !== right.type) return false;
    // Source spans are editor-position metadata. Parser sync can shift them even
    // when the rendered block is unchanged, so block memoization ignores them.

    switch (left.type) {
        case "markdown":
            return right.type === "markdown" && left.content === right.content;
        case "definition":
            return right.type === "definition" && left.id === right.id;
        case "block-render":
        case "inline-render":
            return (
                (right.type === "block-render" || right.type === "inline-render") &&
                left.definitionId === right.definitionId &&
                areStringRecordsEqual(left.attrs, right.attrs)
            );
        case "inline-ref":
            return (
                right.type === "inline-ref" &&
                left.refPath === right.refPath &&
                left.definitionId === right.definitionId &&
                left.accessor === right.accessor &&
                left.outputPath === right.outputPath
            );
        case "for-block":
            return (
                right.type === "for-block" &&
                left.id === right.id &&
                left.each === right.each &&
                left.of === right.of &&
                left.limit === right.limit &&
                left.offset === right.offset &&
                left.concurrency === right.concurrency &&
                left.template === right.template &&
                areStringArraysEqual(left.params, right.params) &&
                left.children.length === right.children.length &&
                left.children.every((child, index) =>
                    areDocumentBlocksRenderEqual(child, right.children[index]),
                )
            );
        default:
            return false;
    }
}

function areStringRecordsEqual(
    left: Record<string, string> | undefined,
    right: Record<string, string> | undefined,
): boolean {
    if (left === right) return true;
    if (!left || !right) return false;
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
}

function areStringArraysEqual(
    left: readonly string[] | undefined,
    right: readonly string[] | undefined,
): boolean {
    if (left === right) return true;
    if (!left || !right || left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
}
